import { FC } from 'react';
import {
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { APP, ROUTES } from 'config';
import {
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import moment from 'moment';
import { useSearchParams } from 'utils/navigation';
import {
  COLOR_BY_CONTENT_REVIEW_STATUS,
  adminContentReviewsFiltersSchema,
  contentReviewEntityTypeLabels,
  contentReviewEntityTypes,
  contentReviewStatuses,
  contentReviewTypeLabel,
  useAdminContentReviews,
} from 'store/admin-content-reviews';

export const AdminContentReviewsList: FC = () => {
  const { t } = useTranslation();
  const { searchParams: filters, navigate } = useSearchParams(
    adminContentReviewsFiltersSchema,
  );

  const { data: reviews, isLoading } = useAdminContentReviews(filters);

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={reviews?.total} />
            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={adminContentReviewsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                keysToExtractFromTotalCalculation={['search']}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('common.status')}
                      value={value.status}
                      initialValue={initialValues.status}
                      onChange={(v) => setFilter('status', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="status"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={contentReviewStatuses.map((status) => ({
                            key: status,
                            label: t(`contentReviews.status.${status}`, {
                              defaultValue: status,
                            }),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('contentReviews.entityType', {
                        defaultValue: 'Type',
                      })}
                      value={value.entityType}
                      initialValue={initialValues.entityType}
                      onChange={(v) => setFilter('entityType', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="entityType"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={contentReviewEntityTypes.map((type) => ({
                            key: type,
                            label: t(`contentReviews.entityType.${type}`, {
                              defaultValue: contentReviewEntityTypeLabels[type],
                            }),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <OpenSearchFilter
                      value={value.search}
                      onApplyFilter={(v) => setFilter('search', v)}
                      placeholder={t('contentReviews.searchPlaceholder', {
                        defaultValue: 'Search by partner or name',
                      })}
                    />
                  </>
                )}
              </Filters>
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={reviews?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'entityName',
            header: t('common.name'),
            cell: ({ row: { original: review } }) => (
              <Link
                label={review.entityName}
                to={ROUTES.admin.contentReviews.details.replace(
                  ':id',
                  review.id,
                )}
              />
            ),
          },
          {
            id: 'entityType',
            header: t('contentReviews.entityType', { defaultValue: 'Type' }),
            cell: ({ row: { original: review } }) => {
              const type = contentReviewTypeLabel(review);
              return t(type.key, { defaultValue: type.defaultValue });
            },
          },
          {
            id: 'supplier',
            header: t('marketplaceItems.supplier'),
            cell: ({ row: { original: review } }) => (
              <Link
                label={review.supplier.name}
                to={ROUTES.admin.suppliers.details.replace(
                  ':id',
                  review.supplier.id,
                )}
              />
            ),
          },
          {
            id: 'submittedAt',
            header: t('contentReviews.submittedAt', {
              defaultValue: 'Submitted',
            }),
            cell: ({ row: { original: review } }) =>
              moment(review.submittedAt).format('l LT'),
            meta: { sort: true },
          },
          {
            id: 'submittedByName',
            header: t('contentReviews.submittedBy', {
              defaultValue: 'Submitted by',
            }),
            cell: ({ row: { original: review } }) =>
              review.submittedByName ?? '—',
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: review } }) => (
              <Typography color={COLOR_BY_CONTENT_REVIEW_STATUS[review.status]}>
                {t(`contentReviews.status.${review.status}`, {
                  defaultValue: review.status,
                })}
              </Typography>
            ),
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          reviews && {
            count: reviews.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
